/**
 * SendForSignatureDialog — Signport Phase A (08/19).
 *
 * Contact-first contract flow. The merchant contact supplies identity and
 * recipient details; the operator enters the new deal terms before sending.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { FileSignature, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  completed: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  signed: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  viewed: 'bg-blue-100 text-blue-800 border-blue-300',
  sent: 'bg-amber-100 text-amber-800 border-amber-300',
  expired: 'bg-gray-100 text-gray-600 border-gray-300',
  canceled: 'bg-red-100 text-red-700 border-red-300',
};

const parseAmount = (value) => {
  const normalized = String(value ?? '').replace(/[$,\s]/g, '');
  if (!normalized) return null;
  const amount = Number(normalized);
  return Number.isFinite(amount) ? amount : null;
};

const formatCalculatedAmount = (value) => {
  if (!Number.isFinite(value)) return '';
  return String(Math.round((value + Number.EPSILON) * 100) / 100);
};

const SendForSignatureDialog = ({ open, onOpenChange, contact, deal, getAuthHeaders, overrides = null }) => {
  const [configured, setConfigured] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [prefill, setPrefill] = useState(null); // {recipient_email, field_meta, unmapped_required}
  const [fieldValues, setFieldValues] = useState({});
  const [calculatedFields, setCalculatedFields] = useState({});
  const [recipient, setRecipient] = useState('');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const subjectId = contact?.id || deal?.id;
  const isContactFlow = Boolean(contact?.id);
  const requestPath = isContactFlow
    ? `${API}/signport/contact/${contact.id}`
    : `${API}/signport/deal/${deal?.id}`;

  const load = useCallback(async () => {
    if (!open || !subjectId) return;
    try {
      const cfg = await axios.get(`${API}/signport/config`, { headers: getAuthHeaders() });
      setConfigured(cfg.data.configured);
      if (!cfg.data.configured) return;
      const [tpls, reqs] = await Promise.all([
        axios.get(`${API}/signport/templates`, { headers: getAuthHeaders() }),
        axios.get(requestPath, { headers: getAuthHeaders() }),
      ]);
      const list = Array.isArray(tpls.data) ? tpls.data : (tpls.data.templates || tpls.data.data || []);
      setTemplates(list);
      setRequests(reqs.data.requests || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reach Signport');
    }
  }, [open, subjectId, requestPath, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const pickTemplate = async (tid) => {
    setTemplateId(tid);
    setPrefill(null);
    setCalculatedFields({});
    setLoading(true);
    try {
      const res = await axios.post(`${API}/signport/prefill`,
        {
          ...(isContactFlow ? { contact_ref: contact.id } : { deal_ref: deal.id }),
          template_id: tid,
          overrides: overrides || undefined,
        },
        { headers: getAuthHeaders() });
      setPrefill(res.data);
      const vals = {};
      (res.data.field_meta || []).forEach(f => { if (f.value !== null && f.value !== undefined) vals[f.key] = f.value; });
      setFieldValues(vals);
      setRecipient(res.data.recipient_email || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load template fields');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    const paybackField = field.semantic === 'funding_amount'
      ? (prefill?.field_meta || []).find(candidate => candidate.semantic === 'payback_amount')
      : null;
    const funding = parseAmount(value);
    const factor = Number(prefill?.calculation?.factor);
    const canCalculatePayback = Boolean(paybackField) && funding !== null && funding >= 0
      && Number.isFinite(factor) && factor > 0;

    setFieldValues(prev => {
      const next = { ...prev, [field.key]: value };
      if (paybackField) {
        next[paybackField.key] = canCalculatePayback
          ? formatCalculatedAmount(funding * factor)
          : '';
      }
      return next;
    });

    if (paybackField) {
      setCalculatedFields(current => ({
        ...current,
        [paybackField.key]: canCalculatePayback,
      }));
    } else if (field.semantic === 'payback_amount') {
      setCalculatedFields(current => ({ ...current, [field.key]: false }));
    }
  };

  // The terms a contract is SENT with, read back off the template's own field
  // semantics. Recorded on the CRM's Signport index so the Contracts dashboard
  // can show funding/payback and pre-fill a funded deal at conversion time.
  // This is send-time context only — contract STATE always comes from Signport.
  const buildTerms = () => {
    const bySemantic = {};
    (prefill?.field_meta || []).forEach(f => {
      if (!f.semantic || bySemantic[f.semantic] !== undefined) return;
      const raw = fieldValues[f.key];
      if (raw !== undefined && raw !== '') bySemantic[f.semantic] = raw;
    });
    const num = (v) => {
      const parsed = parseAmount(v);
      return parsed === null ? undefined : parsed;
    };
    const factor = num(bySemantic.factor);
    return {
      funding_amount: num(bySemantic.funding_amount),
      payback: num(bySemantic.payback_amount),
      factor: factor === undefined ? (prefill?.calculation?.factor ?? undefined) : factor,
      term_days: num(bySemantic.term_days),
      payment_amount: num(bySemantic.payment_amount),
      fees: num(bySemantic.fees),
      schedule: bySemantic.schedule,
    };
  };

  const send = async () => {
    const missing = (prefill?.field_meta || []).filter(
      f => f.required && (fieldValues[f.key] === undefined || fieldValues[f.key] === ''));
    if (missing.length) {
      toast.error(`Fill required field(s): ${missing.map(f => f.label).join(', ')}`);
      return;
    }
    if (!recipient.includes('@')) { toast.error('Valid signer email required'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/signport/send`, {
        ...(isContactFlow ? { contact_ref: contact.id } : { deal_ref: deal.id }),
        template_id: templateId,
        template_name: (() => {
          const t = templates.find(x => (x.id || x.template_id) === templateId);
          return t ? (t.name || t.title || undefined) : undefined;
        })(),
        recipient_email: recipient,
        fields: fieldValues,
        terms: buildTerms(),
      }, { headers: getAuthHeaders() });
      toast.success(`Signing request sent to ${recipient} (status: ${res.data.status})`);
      setTemplateId('');
      setPrefill(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Signport send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="h-5 w-5" />
            {isContactFlow
              ? `Create New Deal — ${contact?.company_name || contact?.name || 'Merchant'}`
              : `Send for Signature — Deal #${deal?.deal_id}`}
          </DialogTitle>
          <DialogDescription>
            {isContactFlow
              ? 'Choose the contract and enter the new deal terms. Merchant details come from this contact.'
              : 'Creates a live signing request on Signport.'}
          </DialogDescription>
        </DialogHeader>

        {configured === false && (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
            Signport isn't configured yet — set <code>SIGNPORT_BASE_URL</code> and{' '}
            <code>SIGNPORT_API_KEY</code> on the Railway backend service, then redeploy.
          </div>
        )}

        {/* Existing requests for this merchant contact */}
        {requests.length > 0 && (
          <div className="border rounded-md divide-y" data-testid="signport-request-list">
            {requests.map(r => (
              <div key={r.id} className="flex items-center justify-between p-2 text-sm">
                <div className="min-w-0">
                  <span className="font-medium">{r.template_id}</span>
                  <span className="text-gray-500 ml-2 text-xs">→ {r.recipient_email} · {String(r.created_at).slice(0, 10)}</span>
                </div>
                <span className="flex items-center gap-1.5 flex-shrink-0">
                  {r.pdf_saved && (
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" title="Executed PDF saved to this deal's Documents tab">
                      📄 in Documents
                    </Badge>
                  )}
                  <Badge className={STATUS_COLORS[(r.status || '').toLowerCase()] || 'bg-gray-100 text-gray-700'}>
                    {r.status}
                  </Badge>
                </span>
              </div>
            ))}
          </div>
        )}

        {configured && (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Template</Label>
              <Select value={templateId} onValueChange={pickTemplate}>
                <SelectTrigger data-testid="signport-template-select">
                  <SelectValue placeholder="Choose a Signport template…" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => {
                    const id = t.id || t.template_id;
                    return <SelectItem key={id} value={id}>{t.name || t.title || id}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>

            {loading && (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-3">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading template fields…
              </div>
            )}

            {prefill && (
              <>
                {prefill.unmapped_required?.length > 0 && (
                  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    Needs your input: {prefill.unmapped_required.join(', ')}
                  </div>
                )}
                <div className="space-y-1">
                  <Label>Signer email</Label>
                  <Input value={recipient} onChange={e => setRecipient(e.target.value)}
                    placeholder="owner@merchant.com" data-testid="signport-recipient-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {(prefill.field_meta || []).map(f => (
                    <div key={f.key} className="space-y-1">
                      <Label className="text-xs">
                        {f.label}{f.required && <span className="text-red-500"> *</span>}
                        {f.source && f.value !== null && f.value !== undefined && (
                          <span className="text-emerald-600 text-[10px] ml-1">· auto</span>
                        )}
                        {calculatedFields[f.key] && (
                          <span className="text-blue-600 text-[10px] ml-1">· calculated</span>
                        )}
                      </Label>
                      <Input
                        value={fieldValues[f.key] ?? ''}
                        onChange={e => updateField(f, e.target.value)}
                        className={f.required && (fieldValues[f.key] === undefined || fieldValues[f.key] === '')
                          ? 'border-amber-400' : ''}
                        data-testid={`signport-field-${f.key}`}
                      />
                      {f.semantic === 'payback_amount' && prefill?.calculation?.factor && (
                        <p className="text-[10px] text-gray-500">
                          Calculates after funding amount is entered (factor {prefill.calculation.factor}); editable.
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                  <Button onClick={send} disabled={sending} data-testid="signport-send-btn">
                    {sending ? 'Sending…' : 'Create & Send'}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SendForSignatureDialog;
